import React, { useCallback, useEffect, useState } from 'react';

import {
  AdminVerificationFacts as Facts,
  BackgroundCheckState,
  JurisdictionVerificationState,
  LivenessState,
  decideBackgroundCheck,
  getAdminVerification,
} from '../api/teacher-verification';
import { Field, Note, Pill, PillTone, Value } from '../../../design-system/ops';

/**
 * Liveness and BackgroundCheck as facts in the application review — UC-A07
 * ADM-07-FR-04 / FR-05 / FR-09 / FR-10.
 *
 * Facts, not a verdict: the reviewer sees what UC-T03 holds and records a
 * background decision through T03's own command (BR-A07-01). Nothing here
 * computes eligibility.
 */

const LIVENESS: Record<LivenessState, { label: string; tone: PillTone }> = {
  NOT_STARTED: { label: 'Sin iniciar', tone: 'neu' },
  PENDING: { label: 'En proceso', tone: 'warn' },
  PASSED: { label: 'Superada', tone: 'ok' },
  FAILED: { label: 'No superada', tone: 'bad' },
  EXPIRED: { label: 'Sesión vencida', tone: 'warn' },
};

const BACKGROUND: Record<BackgroundCheckState, { label: string; tone: PillTone }> = {
  NOT_STARTED: { label: 'Sin iniciar', tone: 'neu' },
  SUBMITTED: { label: 'Enviada', tone: 'tint' },
  UNDER_REVIEW: { label: 'En revisión', tone: 'warn' },
  NEEDS_RESUBMIT: { label: 'Corrección pedida', tone: 'warn' },
  CLEARED: { label: 'Aprobada', tone: 'ok' },
  REJECTED: { label: 'Rechazada (seguridad)', tone: 'bad' },
  EXPIRED: { label: 'Vencida', tone: 'bad' },
};

const JURISDICTION: Record<JurisdictionVerificationState, string> = {
  UNCONFIGURED: 'Sin método registrado',
  MANUAL_VERIFIABLE: 'Verificable con registro oficial',
  PROVIDER_SUPPORTED: 'Cubierto por proveedor',
  UNAVAILABLE: 'Sin verificación posible',
};

const REASON: Record<string, string> = {
  PROVIDER_FINDINGS: 'El proveedor encontró registros — decide Trust & Safety',
  PROVIDER_UNRECOGNISED: 'Estado del proveedor no reconocido — nunca se aprueba solo',
  IDENTIFIER_NOT_VERIFIABLE: 'El proveedor no pudo verificar el número',
  JURISDICTION_UNCONFIGURED: 'País sin método de verificación (ADM-07-FR-10)',
  MANUAL_OFFICIAL_RECORD: 'Verificación manual con registro oficial',
  PROVIDER_UNAVAILABLE: 'El proveedor no respondió; el profesor puede reintentar',
  MANUAL_CLEAR: 'Aprobada manualmente',
  MANUAL_RESUBMIT: 'Corrección pedida manualmente',
  SAFETY_DISQUALIFYING: 'Descalificante por seguridad',
};

const STAGE: Record<string, string> = {
  CURRENT: 'Vigente',
  NOTICE_FIRST: 'Primer aviso (T-60)',
  NOTICE_SECOND: 'Segundo aviso (T-30)',
  RENEWAL_DUE: 'Renovación pendiente (T-7)',
  EXPIRED: 'Vencida',
};

const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString('es-MX') : '—');
const MIN_NOTE = 5;

const AdminVerificationFacts: React.FC<{ teacherId: string }> = ({ teacherId }) => {
  const [facts, setFacts] = useState<Facts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setFacts(await getAdminVerification(teacherId));
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar la verificación.');
    }
  }, [teacherId]);

  useEffect(() => {
    setFacts(null);
    setNote('');
    setInfo(null);
    setError(null);
    void load();
  }, [load]);

  if (!facts) return error ? <Note tone="bad">{error}</Note> : <Note>Cargando verificación…</Note>;

  const { liveness, backgroundCheck, latestAttempt } = facts;
  const decidable =
    latestAttempt?.status === 'SUBMITTED' || latestAttempt?.status === 'UNDER_REVIEW';
  const revocable = backgroundCheck.state === 'CLEARED';
  // AD-A07-07 / AC-13: the server refuses this too; saying so here saves a round trip.
  const clearBlocked =
    latestAttempt?.jurisdiction === 'UNCONFIGURED' || latestAttempt?.jurisdiction === 'UNAVAILABLE';
  const noteShort = note.trim().length < MIN_NOTE;

  const decide = async (decision: 'CLEARED' | 'NEEDS_RESUBMIT' | 'REJECTED', ok: string) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await decideBackgroundCheck(teacherId, { decision, note: note.trim() });
      setNote('');
      await load();
      setInfo(ok);
    } catch (e: any) {
      setError(e?.message || 'No se pudo registrar la decisión.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Field label="Prueba de vida (TCH-04-FR-05)">
        <Value>
          <Pill tone={LIVENESS[liveness.state].tone}>{LIVENESS[liveness.state].label}</Pill>{' '}
          {liveness.confidence !== undefined ? (
            <span className="mut">
              confianza {liveness.confidence.toFixed(1)} / umbral {liveness.threshold}
            </span>
          ) : null}
          {liveness.provider === 'stub' ? (
            <span className="mut"> · proveedor de prueba</span>
          ) : null}
          {liveness.verifiedAt ? (
            <span className="mut"> · verificada {fmt(liveness.verifiedAt)}</span>
          ) : null}
        </Value>
      </Field>

      <Field label="Antecedentes (TCH-03-FR-08)">
        <Value>
          <Pill tone={BACKGROUND[backgroundCheck.state].tone}>
            {BACKGROUND[backgroundCheck.state].label}
          </Pill>{' '}
          <span className="mut">
            {backgroundCheck.jurisdiction.country ?? '—'} ·{' '}
            {JURISDICTION[backgroundCheck.jurisdiction.state]}
            {backgroundCheck.provider ? ` · ${backgroundCheck.provider}` : ''}
          </span>
        </Value>
        {backgroundCheck.reasonCode ? (
          <Value>{REASON[backgroundCheck.reasonCode] ?? backgroundCheck.reasonCode}</Value>
        ) : null}
        {backgroundCheck.expiresAt ? (
          <Value>
            Vence {fmt(backgroundCheck.expiresAt)} · {STAGE[backgroundCheck.renewalStage] ?? '—'}
            {backgroundCheck.renewalInProgress ? ' · renovación en curso' : ''}
          </Value>
        ) : null}
        {backgroundCheck.notices.length > 0 ? (
          <Value>
            <span className="mut">
              Avisos: {backgroundCheck.notices.map((n) => `${n.kind} ${fmt(n.dueAt)}`).join(' · ')}
            </span>
          </Value>
        ) : null}
        {latestAttempt ? (
          <Value>
            <span className="mut">
              Intento {latestAttempt.attemptNumber} · autorización {fmt(latestAttempt.consentedAt)}
              {latestAttempt.reviewedAt ? ` · decidido ${fmt(latestAttempt.reviewedAt)}` : ''}
            </span>
            {latestAttempt.reviewNote ? <div>Nota interna: {latestAttempt.reviewNote}</div> : null}
          </Value>
        ) : null}
      </Field>

      {error ? <Note tone="bad">{error}</Note> : null}
      {info ? <Note tone="warn">{info}</Note> : null}

      {decidable || revocable ? (
        <Field label="Decisión de antecedentes">
          {decidable && clearBlocked ? (
            <Note tone="warn">
              {latestAttempt?.country} no tiene un método de verificación registrado. No se puede
              aprobar caso por caso: regístralo primero en el registro de países (AD-A07-07).
            </Note>
          ) : null}
          <textarea
            className="inp"
            rows={2}
            value={note}
            aria-label="Nota interna de la decisión de antecedentes"
            placeholder="Para el expediente. El profesor no la lee."
            onChange={(e) => setNote(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {decidable ? (
              <>
                <button
                  className="btn btn-p"
                  disabled={busy || noteShort || clearBlocked}
                  onClick={() => decide('CLEARED', 'Antecedentes aprobados.')}
                >
                  Aprobar antecedentes
                </button>
                <button
                  className="btn btn-o"
                  disabled={busy || noteShort}
                  onClick={() => decide('NEEDS_RESUBMIT', 'Corrección pedida.')}
                >
                  Pedir corrección
                </button>
              </>
            ) : null}
            <button
              className="btn btn-d"
              disabled={busy || noteShort}
              onClick={() => decide('REJECTED', 'Antecedentes rechazados por seguridad.')}
            >
              Rechazar por seguridad
            </button>
          </div>
        </Field>
      ) : null}
    </>
  );
};

export default AdminVerificationFacts;
